/**
 * Top-level User Management hook composing data loading and handlers.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { type AvailableLicenseForSeat } from '../../services/admin'
import { useAuth, useIsPlatformAdmin } from '../../hooks'
import type { User } from '../../types'
import type { CreateUserForm, UserManagementOrganisation } from './types'
import { useUserData } from './use-user-data'
import { useUserHandlers } from './use-user-handlers'
import { useToggleStatus } from './use-toggle-status'
import { useModalOpeners } from './use-modal-openers'
import { useUserPageState } from './use-user-page-state'

interface UseUserManagementResult {
  users: User[]
  organisations: UserManagementOrganisation[]
  licenses: import('../../types/admin').License[]
  loading: boolean
  error: string | null
  setError: (msg: string | null) => void
  isPlatformAdmin: boolean
  currentUserId: string | undefined
  showCreateModal: boolean
  setShowCreateModal: (v: boolean) => void
  showEditModal: boolean
  setShowEditModal: (v: boolean) => void
  showDeleteModal: boolean
  setShowDeleteModal: (v: boolean) => void
  showSeatModal: boolean
  setShowSeatModal: (v: boolean) => void
  showReactivateModal: boolean
  setShowReactivateModal: (v: boolean) => void
  showResetPasswordModal: boolean
  issuedPassword: string | null
  setIssuedPassword: (v: string | null) => void
  setShowResetPasswordModal: (v: boolean) => void
  selectedUser: User | null
  setSelectedUser: (user: User | null) => void
  selectedLicense: string
  setSelectedLicense: (id: string) => void
  reactivateLicenses: AvailableLicenseForSeat[]
  setReactivateLicenses: (lics: AvailableLicenseForSeat[]) => void
  userToReactivate: User | null
  setUserToReactivate: (user: User | null) => void
  actionMenuUser: string | null
  setActionMenuUser: (id: string | null) => void
  formData: CreateUserForm
  setFormData: (data: CreateUserForm) => void
  formLoading: boolean
  formError: string | null
  setFormError: (msg: string | null) => void
  resetForm: () => void
  handleCreateUser: (e: React.FormEvent) => Promise<void>
  handleUpdateUser: (e: React.FormEvent) => Promise<void>
  handleDeleteUser: () => Promise<void>
  handleResetPassword: () => Promise<void>
  handleAssignSeat: () => Promise<void>
  handleConfirmReactivate: () => Promise<void>
  handleToggleStatus: (user: User) => Promise<void>
  openEditModal: (user: User) => void
  openDeleteModal: (user: User) => void
  openResetPasswordModal: (user: User) => void
  getOrgName: (orgId: string | null) => string
}

const EMPTY_FORM: CreateUserForm = {
  email: '',
  username: '',
  displayName: '',
  role: 'analyst',
  organisationId: '',
}

export function useUserManagement(searchQuery: string = ''): UseUserManagementResult {
  const { t } = useTranslation('admin')
  const { user: currentUser } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  // FR3: searchQuery (already debounced by the calling page) is passed
  // through to useUserData, which routes it to the server-side search API.
  const data = useUserData(isPlatformAdmin, searchQuery)
  const state = useUserPageState(EMPTY_FORM)

  const resetForm = useCallback((): void => {
    state.setFormData({ ...EMPTY_FORM, organisationId: currentUser?.organisationId ?? '' })
    state.setFormError(null)
  }, [currentUser?.organisationId, state])

  const handlers = useUserHandlers({
    selectedUser: state.selectedUser,
    selectedLicense: state.selectedLicense,
    userToReactivate: state.userToReactivate,
    formData: state.formData,
    setFormLoading: state.setFormLoading,
    setFormError: state.setFormError,
    setIssuedPassword: state.setIssuedPassword,
    setError: data.setError,
    setShowCreateModal: state.setShowCreateModal,
    setShowEditModal: state.setShowEditModal,
    setShowDeleteModal: state.setShowDeleteModal,
    setShowSeatModal: state.setShowSeatModal,
    setShowReactivateModal: state.setShowReactivateModal,
    setShowResetPasswordModal: state.setShowResetPasswordModal,
    setSelectedUser: state.setSelectedUser,
    setSelectedLicense: state.setSelectedLicense,
    setUserToReactivate: state.setUserToReactivate,
    setReactivateLicenses: state.setReactivateLicenses,
    resetForm,
    loadData: data.loadData,
  })

  const handleToggleStatus = useToggleStatus({
    activationStatus: data.activationStatus,
    setError: data.setError,
    setSelectedUser: state.setSelectedUser,
    setShowSeatModal: state.setShowSeatModal,
    setUserToReactivate: state.setUserToReactivate,
    setReactivateLicenses: state.setReactivateLicenses,
    setSelectedLicense: state.setSelectedLicense,
    setShowReactivateModal: state.setShowReactivateModal,
    setActionMenuUser: state.setActionMenuUser,
    loadData: data.loadData,
  })

  const openers = useModalOpeners({
    setSelectedUser: state.setSelectedUser,
    setFormData: state.setFormData,
    setShowEditModal: state.setShowEditModal,
    setShowDeleteModal: state.setShowDeleteModal,
    setShowResetPasswordModal: state.setShowResetPasswordModal,
    setActionMenuUser: state.setActionMenuUser,
  })

  const getOrgName = (orgId: string | null): string => {
    if (!orgId) return t('users.unassigned')
    return data.organisations.find((org) => org.id === orgId)?.name ?? t('users.unknown')
  }

  return {
    users: data.users,
    organisations: data.organisations,
    licenses: data.licenses,
    loading: data.loading,
    error: data.error,
    setError: data.setError,
    isPlatformAdmin,
    currentUserId: currentUser?.id,
    ...state,
    resetForm,
    ...handlers,
    handleToggleStatus,
    ...openers,
    getOrgName,
  }
}
